import type { ContinuousSelection } from 'utils/continuousSelection';
import { Session } from 'types/listings';
import { hasRemainingCapacity, isBookableSessionRow } from 'utils/session';

/**
 * The rows of a group a guest can start a booking in on a date. A scheduled group's are its sessions;
 * a continuously booked group's are the open spans long enough for its shortest booking. The API hands
 * both back as "sessions", so a list of group cards filters through here rather than counting rows.
 */
export const selectBookableRows = (sessions: readonly Session[]): Session[] =>
  sessions.filter(isBookableSessionRow);

/** Whether a group has anything a guest could book on the date: a session, or a bookable open span. */
export const hasBookableAvailability = (
  sessions: readonly Session[],
): boolean => sessions.some(isBookableSessionRow);

interface ResourceAvailabilityParams {
  continuousSelection: ContinuousSelection | null;
  duration?: number;
  isContinuous: boolean;
  resourceId: string;
  sessions: readonly Session[];
  time?: string;
}

/**
 * Whether one resource of a group can take the booking in force, for the map to shade it.
 *
 * A scheduled resource can when it has a session with room at the chosen time and length, or any such
 * session before one is chosen; a cabana held through another group has a session, capped to full. A
 * continuously booked one can when the chosen stay fits inside one of its free spans, or, before a
 * stay is derived, when it has any bookable span at all. A stay's length is never a span's
 * own length, so the scheduled test would read every such resource as booked out.
 */
export const isResourceAvailable = ({
  continuousSelection,
  duration,
  isContinuous,
  resourceId,
  sessions,
  time,
}: ResourceAvailabilityParams): boolean => {
  if (isContinuous) {
    if (continuousSelection) {
      return continuousSelection.spans.some(
        (span) => span.experienceId === resourceId,
      );
    }
    return sessions.some(
      (span) => span.experienceId === resourceId && isBookableSessionRow(span),
    );
  }
  return sessions.some(
    (session) =>
      session.experienceId === resourceId &&
      hasRemainingCapacity(session) &&
      (!duration || session.duration === duration) &&
      (!time || session.startDateTime.split('T')[1] === time),
  );
};
